/**
 * @module ActivateExistingAccountCompleteApiService
 * @remarks Service für die vollständige Aktivierung bestehender Kundenkonten
 */

import { ROUTES } from '@/constants/routes';
import { AccountActivationData, AccountActivationResponse } from '@models/account-activation-complete.model';
import { LoginResponse, RegisterResponse } from '@models/auth.model';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import i18next from 'i18next';
import { protectCityNameForDatabase } from '@/helpers/addressHelpers';

export const activateExistingAccountCompleteApi = createApi({
  reducerPath: 'activateExistingAccountCompleteApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    }
  }),
  tagTypes: ['ActivatedAccount'],
  endpoints: (builder) => ({
    // Kompletter Aktivierungsprozess für ein bestehendes Konto
    activateExistingAccountComplete: builder.mutation<AccountActivationResponse, AccountActivationData>({
      async queryFn(accountData, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          // Schritt 1: Benutzer registrieren mit E-Mail und Passwort
          const registerResult = await fetchWithBQ({
            url: 'auth?path=auth/register',
            method: 'POST',
            body: {
              mail: accountData.email,
              password: accountData.password
            }
          });
          
          if (registerResult.error) {
            return { error: registerResult.error };
          }
          
          const registerData = registerResult.data as RegisterResponse;
          if (registerData.message !== "register successful") {
            return { 
              error: { 
                status: 'CUSTOM_ERROR', 
                error: i18next.t('api-errors.registration-failed') 
              } 
            };
          }
          
          // Schritt 2: Mit den neuen Zugangsdaten einloggen, um den JWT-Token zu erhalten
          const loginResult = await fetchWithBQ({
            url: 'auth?path=auth/login',
            method: 'POST',
            body: {
              mail: accountData.email,
              password: accountData.password
            }
          });
          
          if (loginResult.error) {
            return { error: loginResult.error };
          }
          
          const loginData = loginResult.data as LoginResponse;
          
          // JWT-Token im localStorage speichern
          localStorage.setItem("auth_token", loginData.jwt);
          
          // Schritt 3: Das Kundenprofil mit der Kunden-ID aus dem ersten Schritt aktualisieren (PATCH)
          // Formatierung des Geburtsdatums für die API (deutsches Format)
          const dateOfBirthFormatted = accountData.dateOfBirth ? 
            `${String(accountData.dateOfBirth.getDate()).padStart(2, '0')}.${String(accountData.dateOfBirth.getMonth() + 1).padStart(2, '0')}.${accountData.dateOfBirth.getFullYear()}` : 
            '';
            
          const updateCustomerResult = await fetchWithBQ({
            url: `data/kunden/${accountData.customer_id}`,
            method: 'PATCH',
            headers: {
              Authorization: loginData.jwt
            },
            body: {
              fields: {
                anrede: accountData.salutation,
                vorname: accountData.firstname,
                nachname: accountData.lastname,
                e_mail: accountData.email,
                telefon: accountData.telefon || "",
                strasse_und_hausnummer: accountData.street,
                plz: accountData.postalCode,
                ort: protectCityNameForDatabase(accountData.city),
                country: accountData.country,
                geburtsdatum: dateOfBirthFormatted,
                teilnahmebedingungen: accountData.termsAccepted ? 1 : 0,
                marketing: accountData.marketingAccepted ? 1 : 0,
                users: loginData.user_id
              }
            }
          });
          
          if (updateCustomerResult.error) {
            throw new Error(`Fehler beim Aktualisieren der Kundendaten: ${updateCustomerResult.error}`);
          }
          
          // Schritt 4: User-Datensatz mit der Kunden-ID verknüpfen
          const updateUserResult = await fetchWithBQ({
            url: `data/users/${loginData.user_id}`,
            method: 'PATCH',
            headers: {
              Authorization: loginData.jwt
            },
            body: {
              fields: {
                kunden: accountData.customer_id,
                user_email: accountData.email
              }
            }
          });
          
          if (updateUserResult.error) {
            throw new Error(`Fehler beim Aktualisieren des User-Datensatzes: ${updateUserResult.error}`);
          }
          
          // Die Komponente, die diese Mutation aufruft, sollte sich um das Schließen des Modals
          // und die Navigation kümmern
          
          // Redirect zum My-Account-Bereich
          if (typeof window !== 'undefined') {
            // Wir verwenden setTimeout, um sicherzustellen, dass die Weiterleitung
            // erst nach Abschluss des API-Calls erfolgt
            setTimeout(() => {
              window.location.href = ROUTES.myAccount;
            }, 100);
          }
          
          // Alle Schritte erfolgreich abgeschlossen!
          return { 
            data: { 
              success: true,
              message: i18next.t('account.activation-success'),
              data: {
                userId: loginData.user_id,
                customerId: accountData.customer_id,
                jwt: loginData.jwt
              },
              userId: loginData.user_id,
              customerId: accountData.customer_id,
              jwt: loginData.jwt
            } 
          };
        } catch (error) {
          return { 
            error: { 
              status: 'CUSTOM_ERROR', 
              error: `${i18next.t('api.errors.unexpected-error')}: ${error}` 
            } 
          };
        }
      }
    })
  })
});

// Export hooks für die Verwendung in Komponenten
export const {
  useActivateExistingAccountCompleteMutation
} = activateExistingAccountCompleteApi;
